// Monthly invoicing (Dr. Fletcher physician invoice + NP invoice).
// Filters: month picker. Data: /api/consultations/invoice?month=YYYY-MM.
//
// Physician fees:
//   - Outreach patients registered in the month: $300 each
//   - Pharmacy patients registered in the month: $150 each
//   - No Health Card patients registered in the month: $200 each
//     (includes clients whose card was LATER recovered — the fee stays on the
//     registration month's invoice forever so history never rewrites)
//   - Recovered Card patients whose health card was recovered in the month:
//     credit of -$200 each (uses recovered_date, NOT registration_date;
//     applies to outreach and pharmacy referrals too)
//
// Nurse Practitioner fees:
//   - Outreach patients registered in the month: $50 each
//   - Pharmacy patients registered in the month: $50 each
//   - Health card logic does NOT apply.
//
// PDFs are exported PER SECTION (physician-only and NP-only) so each provider
// has their own standalone invoice. Rendering happens server-side
// (GET /api/consultations/invoice/pdf — WeasyPrint + password protection), so
// the output is identical on every device and never depends on screen size.
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  FileDown,
  DollarSign,
  Check,
  Trash2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/consult/api";
import { getToken } from "@/lib/consult/authSession";
import TableColumnHeader from "@/pages/consult/TableColumnHeader";
import { useTableView, applyTableView, uniqueValues, hasActiveFilters } from "@/pages/consult/tableView";

const DARK_GREEN = "#0F3B2E";
const LABEL_GREEN = "#2D5A4C";
const TEXT = "#1C2B26";
const MUTED = "#6B7A73";
const BORDER = "#E5E2DC";
const COMPLETED_COL = "#2E7D5B";
const MISSED_COL = "#B4351B";

// Case-management status shown as a pill between Patient and Medication,
// with a filter dropdown on the column header.
const STATUS_LABEL = {
  scheduled: "Scheduled",
  completed: "Completed",
  missed: "Missed",
  not_required: "Not required",
  hold: "Hold",
  not_qualified: "Not qualified",
};
const STATUS_COLOR = {
  scheduled: "#1F4E79",
  completed: COMPLETED_COL,
  missed: MISSED_COL,
  not_required: MUTED,
  hold: "#8A5A00",
  not_qualified: MISSED_COL,
};

// Case-management pipeline stage (furthest ticked flag) — same order/colors
// as the record dialog's "Case management" dropdown. "" = Pending.
const CASE_STAGES = [
  ["refused", "Refused", "#717D7E"],
  ["external_tx", "External Tx", "#CA6F1E"],
  ["no_coverage", "No Coverage", "#566573"],
  ["bloodwork", "Bloodwork", "#C0392B"],
  ["genotype", "Genotype", "#00838F"],
  ["resistance", "Resistance", "#2E86C1"],
  ["consultation", "Consultation", "#4E9A51"],
  ["prescription", "Prescription", "#B4436C"],
  ["reimbursement", "Reimbursement", "#9C6644"],
  ["compassionate", "Compassionate", "#0E7490"],
  ["submitted", "Submitted", "#4A6FB0"],
  ["manual_claim", "Manual claim", "#B7950B"],
  ["delivery", "Delivery", "#884EA0"],
  ["pharmacy", "Pharmacy", "#6C3483"],
  ["dispensed", "Dispensed", "#2A9F92"],
];
const CASE_LABEL = { "": "Pending", ...Object.fromEntries(CASE_STAGES.map(([v, l]) => [v, l])) };
const CASE_COL = { "": "#E58A2E", ...Object.fromEntries(CASE_STAGES.map(([v, , c]) => [v, c])) };

// Shared fixed column widths so EVERY invoice table (physician outreach /
// pharmacy / no health card, nurse-practitioner sections) lines up exactly —
// and applying filters can never shift a column. Consultation is the widest
// header label so it gets extra room; Recovered + Verified stay slim.
const COLS_STD = [20, 10, 12, 12, 7, 12, 11, 7, 9];    // Patient · Case mgmt · Consultation · Medication · Months · Scheduled by · Reg. date · Verified · Fee
const COLS_REC = [18, 10, 11, 11, 6, 11, 10, 8, 6, 9]; // + Recovered date (Recovered Card credit table only)

const labelStyle = {
  fontSize: 12, fontWeight: 700, letterSpacing: "0.24em",
  textTransform: "uppercase", color: LABEL_GREEN, margin: 0,
};

const fmtMoney = (n) => {
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString("en-CA")}`;
};

// "2026-09-14T…" → "Sep 14" (for the Verified stamp line)
const fmtVerifDate = (iso) => {
  const ymd = String(iso || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "";
  try { return format(new Date(`${ymd}T12:00:00`), "MMM d"); } catch { return ymd; }
};
const initials = (name) =>
  String(name || "").trim().split(/\s+/).map((w) => w[0] || "").join("").slice(0, 3).toUpperCase();

const monthShift = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function InvoicingPanel({ onOpenAppointment, refreshKey = 0 }) {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    const m = month;
    setLoading(true);
    try {
      const { data } = await api.get("/invoice", { params: { month: m } });
      // Ignore stale responses — an older month's fetch that resolves AFTER
      // a newer one must not overwrite it (dev double-mount + fast Prev/Next).
      if (seq === loadSeq.current) setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load invoice.");
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  // Re-fetch when the Dashboard toolbar Refresh button bumps refreshKey.
  useEffect(() => {
    if (refreshKey === 0) return;
    load();
  }, [refreshKey, load]);

  const monthLabel = useMemo(() => {
    try {
      return format(new Date(`${month}-01`), "MMMM yyyy");
    } catch { return month; }
  }, [month]);

  const grandTotal = (data?.physician?.total || 0) + (data?.nurse?.total || 0);

  // Months-of-medication dropdown (screen-only column, options are managed
  // like the Medication list: 3 / 2 / 1 seeded, add / rename / delete).
  const [monthsOpts, setMonthsOpts] = useState([]);
  const [monthsSaving, setMonthsSaving] = useState(null);
  const [manageMonthsOpen, setManageMonthsOpen] = useState(false);
  const loadMonthsOpts = useCallback(async () => {
    try {
      const { data } = await api.get("/months-options");
      setMonthsOpts(data.items || []);
    } catch { /* non-fatal — dropdown just stays empty */ }
  }, []);
  useEffect(() => { loadMonthsOpts(); }, [loadMonthsOpts]);

  const changeMonths = useCallback(async (row, val) => {
    if (monthsSaving) return;
    setMonthsSaving(row.id);
    try {
      await api.patch(`/appointments/${row.id}`, { medication_months: val });
      setData((prev) => {
        if (!prev) return prev;
        const patchRows = (rows) => (rows || []).map((r) => r.id === row.id
          ? { ...r, medication_months: val }
          : r);
        const patchSection = (sec) => {
          const out = { ...sec };
          for (const k of ["outreach", "pharmacy", "unknown", "no_card", "recovered", "replacement"]) {
            if (out[k]?.rows) out[k] = { ...out[k], rows: patchRows(out[k].rows) };
          }
          return out;
        };
        return { ...prev, physician: patchSection(prev.physician), nurse: patchSection(prev.nurse) };
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update months.");
    } finally {
      setMonthsSaving(null);
    }
  }, [monthsSaving]);

  // Billing reconciliation — tick "Verify" once a row matches the provider's
  // actual billing for the month. Stamps are stored per (appointment, month)
  // so every bucket the patient appears in updates at once.
  const [verifSaving, setVerifSaving] = useState(null);
  const toggleVerified = useCallback(async (row) => {
    if (verifSaving) return;
    setVerifSaving(row.id);
    try {
      const { data: v } = await api.put("/invoice/verify", {
        appointment_id: row.id, month, verified: !row.verified,
      });
      setData((prev) => {
        if (!prev) return prev;
        const patchRows = (rows) => (rows || []).map((r) => r.id === row.id
          ? { ...r, verified: v.verified, verified_at: v.verified_at, verified_by: v.verified_by }
          : r);
        const patchSection = (sec) => {
          const out = { ...sec };
          for (const k of ["outreach", "pharmacy", "unknown", "no_card", "recovered", "replacement"]) {
            if (out[k]?.rows) out[k] = { ...out[k], rows: patchRows(out[k].rows) };
          }
          return out;
        };
        return { ...prev, physician: patchSection(prev.physician), nurse: patchSection(prev.nurse) };
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update verification.");
    } finally {
      setVerifSaving(null);
    }
  }, [verifSaving, month]);

  const [exportingWhich, setExportingWhich] = useState(null); // 'physician' | 'nurse' | null

  // Server-rendered PDF (WeasyPrint on the backend): deterministic A4 layout,
  // real selectable text, rows that never split across pages, identical output
  // on every device, password-protected per provider (fletcherd / timothyj).
  const exportPDF = (which) => {
    const t = getToken();
    if (!t) {
      toast.error("Session expired. Please sign in again.");
      return;
    }
    setExportingWhich(which);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/consultations/invoice/pdf`
      + `?month=${month}&which=${which}&token=${encodeURIComponent(t)}`;
    // Plain GET download so iOS Safari handles it natively (the endpoint
    // accepts the session token as a query param, like other downloads).
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // The render takes ~1-2s server-side; the browser shows download progress.
    setTimeout(() => setExportingWhich(null), 3000);
  };

  return (
    <div style={{ display: "grid", gap: 16, minWidth: 0 }}>
      {/* Scoped CSS for the horizontal scroll wrappers (mobile-friendly). */}
      <style>{`
        /* Horizontal scroll wrapper — mobile-friendly.
           Uses default touch-action so iOS/Android detect swipes reliably,
           shows a thin persistent scrollbar so users see the affordance,
           and adds a right-edge fade + tiny "swipe" chip until scrolled. */
        .invoice-hscroll {
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior-x: contain;
          scrollbar-width: thin;
          scrollbar-color: rgba(15,59,46,0.35) transparent;
        }
        .invoice-hscroll::-webkit-scrollbar { height: 8px; }
        .invoice-hscroll::-webkit-scrollbar-track { background: transparent; }
        .invoice-hscroll::-webkit-scrollbar-thumb {
          background: rgba(15,59,46,0.35);
          border-radius: 4px;
        }
        .invoice-hscroll-wrap {
          position: relative;
          /* Bleed to card's inner edge so horizontal scroll reaches every column */
          margin: 0 -20px;
          padding: 0 20px;
        }
        .invoice-hscroll-wrap.compact {
          margin: 10px -16px 0;
          padding: 0 16px;
        }
        /* Right-edge fade cue — hides itself once the user scrolls to the end
           via the [data-scrolled-end] attribute we toggle from JS. */
        .invoice-hscroll-wrap::after {
          content: "";
          position: absolute;
          top: 0; right: 0; bottom: 8px;
          width: 28px;
          background: linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.95) 100%);
          pointer-events: none;
          opacity: 1;
          transition: opacity 0.18s ease;
        }
        .invoice-hscroll-wrap[data-scrolled-end="true"]::after { opacity: 0; }
        .invoice-hscroll-wrap[data-scrollable="false"]::after { opacity: 0; }
      `}</style>

      {/* Months options manager (opened from any Months dropdown → Manage…) */}
      <ManageMonthsDialog
        open={manageMonthsOpen}
        onOpenChange={setManageMonthsOpen}
        options={monthsOpts}
        onChanged={loadMonthsOpts}
      />

      {/* Month picker + actions */}
      <div
        className="invoice-toolbar"
        style={{
          background: "white",
          borderRadius: 14,
          border: `1px solid ${BORDER}`,
          padding: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <p style={{ ...labelStyle, marginBottom: 4 }}>Invoice month</p>
          <div style={{
            fontFamily: "'Work Sans', system-ui, sans-serif",
            fontSize: 22, fontWeight: 700, color: TEXT, letterSpacing: "-0.01em",
            display: "flex", alignItems: "baseline", gap: 10,
          }}>
            {monthLabel}
            {loading && data && (
              <span data-testid="invoice-updating" style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: "uppercase", letterSpacing: ".06em" }}>
                Updating…
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Button
            variant="outline"
            onClick={() => setMonth(monthShift(month, -1))}
            data-testid="invoice-prev"
          >
            <ChevronLeft style={{ width: 15, height: 15, marginRight: 4 }} /> Prev
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const d = new Date();
              setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
            }}
            data-testid="invoice-this-month"
          >
            This month
          </Button>
          <Button
            variant="outline"
            onClick={() => setMonth(monthShift(month, 1))}
            data-testid="invoice-next"
          >
            Next <ChevronRight style={{ width: 15, height: 15, marginLeft: 4 }} />
          </Button>
        </div>
      </div>

      {/* Keep the current data on screen while a refresh runs (e.g. after
          saving a patient profile from a row) — swapping to a short Loading
          card would collapse the page height and snap the scroll to the top. */}
      {!data ? (
        <div style={{
          background: "white", borderRadius: 14, border: `1px solid ${BORDER}`,
          padding: 40, textAlign: "center", color: MUTED,
        }}>
          Loading…
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
          {/* On-screen grand total (not part of either PDF export) */}
          <div style={{
            background: DARK_GREEN, borderRadius: 14, padding: 20,
            color: "white",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            flexWrap: "wrap", gap: 12,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10, background: "rgba(255,255,255,0.14)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <DollarSign style={{ width: 22, height: 22 }} strokeWidth={2} />
              </div>
              <div>
                <p style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: "0.24em",
                  textTransform: "uppercase", opacity: 0.7, margin: 0,
                }}>
                  Grand total · {monthLabel}
                </p>
                <div
                  data-testid="invoice-grand-total"
                  style={{
                    fontFamily: "'Work Sans', system-ui, sans-serif",
                    fontSize: 28, fontWeight: 700, marginTop: 2,
                  }}
                >
                  {fmtMoney(grandTotal)}
                </div>
              </div>
            </div>
            <div style={{ fontSize: 13, opacity: 0.85 }}>
              Physician {fmtMoney(data.physician.total)} · NP {fmtMoney(data.nurse.total)}
            </div>
          </div>

          {/* Physician invoice — self-contained PDF section */}
          <div>
            <SectionHeader
              title="Physician — Dr. Fletcher"
              subtitle={monthLabel}
              total={data.physician.total}
              onExport={() => exportPDF("physician")}
              exporting={exportingWhich === "physician"}
              disabled={exportingWhich !== null}
              testId="invoice-physician-pdf"
            />
            <InvoiceSection
              testId="invoice-physician"
              rows={[
                { label: "Outreach", count: data.physician.outreach.count, rate: `$${data.physician.rates.outreach}`, subtotal: data.physician.outreach.subtotal, patients: data.physician.outreach.rows, color: DARK_GREEN },
                { label: "Pharmacy", count: data.physician.pharmacy.count, rate: `$${data.physician.rates.pharmacy}`, subtotal: data.physician.pharmacy.subtotal, patients: data.physician.pharmacy.rows, color: COMPLETED_COL },
                { label: "Replacement", count: data.physician.replacement?.count || 0, rate: "$100", subtotal: data.physician.replacement?.subtotal || 0, patients: data.physician.replacement?.rows || [], color: "#4A6FB0" },
                { label: "Unknown referral", count: data.physician.unknown?.count || 0, rate: "—", subtotal: data.physician.unknown?.subtotal || 0, patients: data.physician.unknown?.rows || [], color: "#8A5A00" },
                { label: "No Health Card", count: data.physician.no_card.count, rate: `$${data.physician.rates.no_card}`, subtotal: data.physician.no_card.subtotal, patients: data.physician.no_card.rows, color: MISSED_COL },
                { label: "Recovered Card (credit)", count: data.physician.recovered.count, rate: `-$${Math.abs(data.physician.rates.recovered)}`, subtotal: data.physician.recovered.subtotal, patients: data.physician.recovered.rows, color: "#8A5A00" },
              ]}
              total={data.physician.total}
              onOpenAppointment={onOpenAppointment}
              onToggleVerified={toggleVerified}
              verifSavingId={verifSaving}
              monthsOpts={monthsOpts}
              onChangeMonths={changeMonths}
              monthsSavingId={monthsSaving}
              onManageMonths={() => setManageMonthsOpen(true)}
            />
          </div>

          {/* Nurse Practitioner invoice — self-contained PDF section */}
          <div>
            <SectionHeader
              title="Nurse Practitioner"
              subtitle={monthLabel}
              total={data.nurse.total}
              onExport={() => exportPDF("nurse")}
              exporting={exportingWhich === "nurse"}
              disabled={exportingWhich !== null}
              testId="invoice-nurse-pdf"
            />
            <InvoiceSection
              testId="invoice-nurse"
              rows={[
                { label: "Outreach", count: data.nurse.outreach.count, rate: `$${data.nurse.rates.outreach}`, subtotal: data.nurse.outreach.subtotal, patients: data.nurse.outreach.rows, color: DARK_GREEN },
                { label: "Pharmacy", count: data.nurse.pharmacy.count, rate: `$${data.nurse.rates.pharmacy}`, subtotal: data.nurse.pharmacy.subtotal, patients: data.nurse.pharmacy.rows, color: COMPLETED_COL },
                { label: "Unknown referral", count: data.nurse.unknown?.count || 0, rate: "—", subtotal: data.nurse.unknown?.subtotal || 0, patients: data.nurse.unknown?.rows || [], color: "#8A5A00" },
              ]}
              total={data.nurse.total}
              onOpenAppointment={onOpenAppointment}
              onToggleVerified={toggleVerified}
              verifSavingId={verifSaving}
              monthsOpts={monthsOpts}
              onChangeMonths={changeMonths}
              monthsSavingId={monthsSaving}
              onManageMonths={() => setManageMonthsOpen(true)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Per-section header: clean text block with month, provider title, and
// Small horizontal-scroll wrapper that:
//   - lets the browser handle touch panning natively (no touch-action lock)
//   - shows a thin visible scrollbar so users see the affordance
//   - toggles a data-scrollable/data-scrolled-end flag so the CSS
//     right-edge fade cue disappears once the user reaches the end
//     (or when the content doesn't need to scroll at all).
function HScroll({ compact, children }) {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return;
    const update = () => {
      const scrollable = inner.scrollWidth - inner.clientWidth > 2;
      const atEnd = inner.scrollLeft + inner.clientWidth >= inner.scrollWidth - 2;
      wrap.setAttribute("data-scrollable", scrollable ? "true" : "false");
      wrap.setAttribute("data-scrolled-end", scrollable && atEnd ? "true" : "false");
    };
    update();
    inner.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(inner);
    return () => {
      inner.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      className={`invoice-hscroll-wrap${compact ? " compact" : ""}`}
      data-scrollable="false"
      data-scrolled-end="false"
    >
      <div ref={innerRef} className="invoice-hscroll">
        {children}
      </div>
    </div>
  );
}

// section total on separate lines — no card / border / background so it
// reads as a heading rather than a widget.
function SectionHeader({ title, subtitle, total, onExport, exporting, disabled, testId }) {
  return (
    <div
      style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        gap: 16, flexWrap: "wrap",
        padding: "4px 4px 0 4px",
        marginBottom: 12,
      }}
    >
      <div>
        <p style={{
          fontSize: 11, fontWeight: 700, letterSpacing: "0.24em",
          textTransform: "uppercase", color: MUTED, margin: 0, marginBottom: 6,
        }}>
          {subtitle}
        </p>
        <p style={{
          fontSize: 13, fontWeight: 700, letterSpacing: "0.24em",
          textTransform: "uppercase", color: TEXT, margin: 0, marginBottom: 6,
        }}>
          {title}
        </p>
        <p style={{
          fontSize: 11, fontWeight: 700, letterSpacing: "0.24em",
          textTransform: "uppercase", color: MUTED, margin: 0,
        }}>
          Total {fmtMoney(total)}
        </p>
      </div>
      <div>
        <Button
          onClick={onExport}
          disabled={disabled}
          data-testid={testId}
          style={{ background: DARK_GREEN, color: "white" }}
        >
          <FileDown style={{ width: 14, height: 14, marginRight: 6 }} />
          {exporting ? "Rendering…" : "Download PDF"}
        </Button>
      </div>
    </div>
  );
}

/**
 * Sortable + filterable + searchable patients table for a single invoice
 * category section (Outreach, Pharmacy, Replacement, etc.). View state
 * (sort key + filter value + search) is persisted per section in
 * sessionStorage so switching months doesn't reset your active view.
 */
function SortableRowsTable({ sectionKey, patients, showRecoveredCol, isRecovered, onOpenAppointment, onToggleVerified, verifSavingId, monthsOpts, onChangeMonths, monthsSavingId, onManageMonths }) {
  const [view, updateView] = useTableView(`invoice.section.${sectionKey}.view.v1`);
  const tableColumns = useMemo(() => ({
    patient_name: { get: (r) => r.patient_name || "" },
    case_stage: { get: (r) => r.case_stage || "" },
    status: { get: (r) => r.status || "" },
    medication: { get: (r) => r.medication || "" },
    medication_months: { get: (r) => r.medication_months || "" },
    scheduled_by: { get: (r) => r.scheduled_by || "" },
    registration_date: { get: (r) => r.registration_date, type: "date" },
    verified: { get: (r) => (r.verified ? "Verified" : "Not verified") },
    fee: { get: (r) => Number(r.fee) || 0, type: "number" },
  }), []);
  const displayRecords = useMemo(
    () => applyTableView(patients, view, tableColumns, ["patient_name", "case_stage", "status", "medication", "scheduled_by", "registration_date"]),
    [patients, view, tableColumns]
  );
  const uniqNames    = useMemo(() => uniqueValues(patients, (r) => r.patient_name), [patients]);
  const uniqCase     = useMemo(() => uniqueValues(patients, (r) => r.case_stage || "")
    .map((v) => ({ value: v, label: CASE_LABEL[v] || v })), [patients]);
  const uniqStatus   = useMemo(() => uniqueValues(patients, (r) => r.status)
    .map((v) => ({ value: v, label: STATUS_LABEL[v] || v })), [patients]);
  const uniqMeds     = useMemo(() => uniqueValues(patients, (r) => r.medication), [patients]);
  const uniqSched    = useMemo(() => uniqueValues(patients, (r) => r.scheduled_by), [patients]);
  const uniqRegDates = useMemo(() => uniqueValues(patients, (r) => r.registration_date), [patients]);

  return (
    <>
      {/* Search / clear / count toolbar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8, padding: "6px 0 8px",
        flexWrap: "nowrap",
      }}>
        <input
          data-testid={`invoice-section-${sectionKey}-search`}
          type="text"
          value={view.search || ""}
          onChange={(e) => updateView({ search: e.target.value })}
          placeholder="Search…"
          style={{
            flex: 1, minWidth: 0, height: 32,
            padding: "0 10px", fontSize: 12,
            border: `1px solid ${BORDER}`, borderRadius: 6,
            background: "#fff", color: TEXT, boxSizing: "border-box",
          }}
        />
        {(view.sortKey || hasActiveFilters(view) || view.search) && (
          <button
            type="button"
            data-testid={`invoice-section-${sectionKey}-clear`}
            onClick={() => updateView({ sortKey: null, sortDir: null, filters: {}, search: "" })}
            style={{
              background: "rgba(180,53,27,0.08)", border: `1px solid #E9BFB1`,
              borderRadius: 6, padding: "4px 8px",
              fontSize: 11, fontWeight: 700, color: "#B4351B", cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Clear
          </button>
        )}
        <div style={{ fontSize: 11, color: MUTED, whiteSpace: "nowrap" }}>
          {displayRecords.length} of {patients.length}
        </div>
      </div>
      <HScroll compact>
      {/* tableLayout fixed + shared colgroup = identical column alignment in
          every section (outreach / pharmacy / no health card / NP), and
          choosing filters never shifts a column. minWidth guarantees the
          wrapper scrolls horizontally before any column can squish. */}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: showRecoveredCol ? 1080 : 1000, tableLayout: "fixed" }}>
        <colgroup>
          {(showRecoveredCol ? COLS_REC : COLS_STD).map((w, i) => <col key={i} style={{ width: `${w}%` }} />)}
        </colgroup>
        <thead>
          <tr style={{ color: MUTED, textAlign: "left" }}>
            <TableColumnHeader columnKey="patient_name" label="Patient" values={uniqNames} view={view} onChange={updateView} testId={`invoice-${sectionKey}`} />
            <TableColumnHeader columnKey="case_stage" label="Case mgmt" values={uniqCase} view={view} onChange={updateView} testId={`invoice-${sectionKey}`} />
            <TableColumnHeader columnKey="status" label="Consultation" values={uniqStatus} view={view} onChange={updateView} testId={`invoice-${sectionKey}`} />
            <TableColumnHeader columnKey="medication" label="Medication" values={uniqMeds} view={view} onChange={updateView} testId={`invoice-${sectionKey}`} />
            <TableColumnHeader columnKey="medication_months" label="Months" values={(monthsOpts || []).map((o) => o.name)} view={view} onChange={updateView} testId={`invoice-${sectionKey}`} />
            <TableColumnHeader columnKey="scheduled_by" label="Scheduled by" values={uniqSched} view={view} onChange={updateView} testId={`invoice-${sectionKey}`} />
            <TableColumnHeader columnKey="registration_date" label="Reg. date" type="date" values={uniqRegDates} view={view} onChange={updateView} testId={`invoice-${sectionKey}`} />
            {showRecoveredCol && (
              <th style={{ padding: "6px 8px", fontWeight: 600, whiteSpace: "nowrap", textAlign: "left", overflow: "hidden", textOverflow: "ellipsis" }}>Recovered</th>
            )}
            {/* Internal billing reconciliation — not part of the server-rendered PDF */}
            <TableColumnHeader columnKey="verified" label="Verified" values={["Not verified", "Verified"]} view={view} onChange={updateView} testId={`invoice-${sectionKey}`} />
            <th style={{ padding: "6px 16px 6px 8px", fontWeight: 600, textAlign: "right", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Fee</th>
          </tr>
        </thead>
        <tbody>
          {displayRecords.length === 0 && (
            <tr><td colSpan={showRecoveredCol ? 10 : 9} style={{ padding: 12, textAlign: "center", color: MUTED, fontSize: 12 }}>
              No rows match your search.
            </td></tr>
          )}
          {displayRecords.map((p) => (
            <tr key={p.id} style={{ borderTop: `1px solid ${BORDER}` }}>
              <td style={{ padding: "6px 8px", color: TEXT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {onOpenAppointment ? (
                  <button
                    type="button"
                    onClick={() => onOpenAppointment(p)}
                    data-testid={`invoice-patient-${p.id}`}
                    title="Open patient profile"
                    style={{
                      background: "none", border: "none", padding: 0,
                      color: DARK_GREEN, cursor: "pointer",
                      fontWeight: 600, textDecoration: "underline",
                      fontFamily: "inherit", fontSize: "inherit",
                      textAlign: "left", whiteSpace: "nowrap",
                    }}
                  >
                    {p.patient_name}
                  </button>
                ) : (
                  p.patient_name
                )}
              </td>
              <td style={{ padding: "6px 8px", whiteSpace: "nowrap", overflow: "hidden" }} data-testid={`invoice-casestage-${p.id}`}>
                <span style={{
                  display: "inline-block", maxWidth: "100%",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  verticalAlign: "middle",
                  fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em",
                  color: CASE_COL[p.case_stage || ""] || MUTED,
                  border: `1.5px solid ${CASE_COL[p.case_stage || ""] || MUTED}`,
                  borderRadius: 99, padding: "1px 8px",
                }}>
                  {CASE_LABEL[p.case_stage || ""]}
                </span>
              </td>
              <td style={{ padding: "6px 8px", whiteSpace: "nowrap", overflow: "hidden" }} data-testid={`invoice-status-${p.id}`}>
                <span style={{
                  display: "inline-block", maxWidth: "100%",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  verticalAlign: "middle",
                  fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em",
                  color: STATUS_COLOR[p.status] || MUTED,
                  border: `1.5px solid ${STATUS_COLOR[p.status] || MUTED}`,
                  borderRadius: 99, padding: "1px 8px",
                }}>
                  {STATUS_LABEL[p.status] || p.status || "—"}
                </span>
              </td>
              <td style={{ padding: "6px 8px", color: MUTED, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.medication || "—"}</td>
              {/* Months of medication dispensed — inline dropdown, screen-only
                  (never on the PDF). "Manage…" opens the options manager. */}
              <td style={{ padding: "2px 4px", whiteSpace: "nowrap", overflow: "hidden" }}>
                <select
                  value={p.medication_months || ""}
                  disabled={monthsSavingId === p.id}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "__manage") { onManageMonths?.(); return; }
                    onChangeMonths?.(p, v);
                  }}
                  data-testid={`invoice-months-${p.id}`}
                  style={{
                    fontFamily: "inherit", fontSize: 13, cursor: "pointer",
                    color: p.medication_months ? TEXT : MUTED,
                    background: "transparent", border: `1px solid ${BORDER}`,
                    borderRadius: 6, padding: "3px 4px", maxWidth: "100%",
                    opacity: monthsSavingId === p.id ? 0.5 : 1,
                  }}
                >
                  <option value="">—</option>
                  {(monthsOpts || []).map((o) => (
                    <option key={o.id} value={o.name}>{o.name}</option>
                  ))}
                  <option value="__manage">Manage…</option>
                </select>
              </td>
              <td style={{ padding: "6px 8px", color: MUTED, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.scheduled_by || "—"}</td>
              <td style={{ padding: "6px 8px", color: MUTED, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.registration_date}</td>
              {showRecoveredCol && (
                <td style={{ padding: "6px 8px", color: MUTED, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {isRecovered ? (p.recovered_date || "—") : "—"}
                </td>
              )}
              <td style={{ padding: "6px 8px", whiteSpace: "nowrap", overflow: "hidden" }} data-testid={`invoice-verified-${p.id}`}>
                {p.verified ? (
                  <button
                    type="button"
                    onClick={() => onToggleVerified?.(p)}
                    disabled={verifSavingId === p.id}
                    title="Verified against billing — click to undo"
                    data-testid={`invoice-unverify-${p.id}`}
                    style={{
                      display: "inline-flex", flexDirection: "column", gap: 1,
                      cursor: "pointer", background: "none", border: 0, padding: 0,
                      fontFamily: "inherit", textAlign: "left",
                    }}
                  >
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      background: "rgba(46,125,91,0.12)", color: COMPLETED_COL,
                      borderRadius: 999, padding: "2px 9px",
                      fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em",
                    }}>
                      <Check style={{ width: 10, height: 10 }} strokeWidth={3} />
                      Verified
                    </span>
                    <span style={{ fontSize: 10, color: MUTED, paddingLeft: 4 }}>
                      {fmtVerifDate(p.verified_at)}{p.verified_by ? ` · ${initials(p.verified_by)}` : ""}
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onToggleVerified?.(p)}
                    disabled={verifSavingId === p.id}
                    title="Confirm this row matches the provider's billing"
                    data-testid={`invoice-verify-${p.id}`}
                    style={{
                      fontFamily: "inherit", cursor: "pointer",
                      fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em",
                      border: `1.5px solid ${COMPLETED_COL}`, color: COMPLETED_COL,
                      background: "#fff", borderRadius: 999, padding: "3px 10px",
                      display: "inline-flex", alignItems: "center", gap: 4,
                    }}
                  >
                    Verify
                  </button>
                )}
              </td>
              <td style={{ padding: "6px 16px 6px 8px", textAlign: "right", color: TEXT, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {fmtMoney(p.fee)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </HScroll>
    </>
  );
}

// Manage the Months dropdown options (3 / 2 / 1 by default) — add, rename
// (cascades onto every patient row), or delete. Mirrors Manage medications.
function ManageMonthsDialog({ open, onOpenChange, options, onChanged }) {
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setRows((options || []).map((m) => ({ ...m, _draft: m.name })));
  }, [open, options]);

  const saveRow = async (r) => {
    if (!r._draft.trim() || r._draft.trim() === r.name) return;
    setBusy(true);
    try {
      await api.patch(`/months-options/${r.id}`, { name: r._draft.trim() });
      toast.success("Option renamed — patient rows updated too");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteRow = async (r) => {
    if (!window.confirm(`Delete months option "${r.name}"? Patient rows keep their saved value.`)) return;
    setBusy(true);
    try {
      await api.delete(`/months-options/${r.id}`);
      toast.success("Option deleted");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const addRow = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/months-options", { name });
      setNewName("");
      toast.success("Option added");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="invoice-manage-months-dialog" className="max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage months options</DialogTitle>
          <DialogDescription>
            Add, rename or delete. Shared across every Months dropdown. Renames update existing patient rows automatically.
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          {rows.length === 0 && (
            <div style={{ color: "#6B7A73", fontSize: 13, padding: 12, textAlign: "center" }}>
              No options yet — add one below.
            </div>
          )}
          {rows.map((r, idx) => (
            <div
              key={r.id}
              data-testid={`invoice-months-row-${r.id}`}
              style={{
                display: "flex", gap: 8, alignItems: "center",
                padding: "8px 10px", border: "1px solid #E5E2DC", borderRadius: 8,
              }}
            >
              <input
                value={r._draft}
                onChange={(e) => setRows((rs) => rs.map((x, i) => i === idx ? { ...x, _draft: e.target.value } : x))}
                onBlur={() => saveRow(r)}
                data-testid={`invoice-months-input-${r.id}`}
                style={{ flex: 1, border: 0, padding: 0, background: "transparent", fontSize: 14, color: "#1C2B26", outline: "none" }}
              />
              {r._draft.trim() && r._draft.trim() !== r.name && (
                <button
                  type="button"
                  onClick={() => saveRow(r)}
                  data-testid={`invoice-months-save-${r.id}`}
                  title="Save"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#2E7D5B", padding: 4 }}
                >
                  <Check style={{ width: 15, height: 15 }} strokeWidth={2.5} />
                </button>
              )}
              <button
                type="button"
                onClick={() => deleteRow(r)}
                data-testid={`invoice-months-delete-${r.id}`}
                title="Delete"
                style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7A73", padding: 4 }}
              >
                <Trash2 style={{ width: 15, height: 15 }} strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <input
            data-testid="invoice-new-months-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addRow(); }}
            placeholder="Add new option…"
            style={{ flex: 1, height: 38, padding: "0 12px", fontSize: 14, borderRadius: 8, border: "1px solid #E5E2DC", background: "#fff" }}
          />
          <button
            type="button"
            data-testid="invoice-new-months-add"
            onClick={addRow}
            disabled={!newName.trim() || busy}
            style={{
              background: "#0F3B2E", color: "#fff", border: 0,
              borderRadius: 8, padding: "0 16px", fontSize: 13, fontWeight: 600,
              cursor: !newName.trim() || busy ? "default" : "pointer",
              opacity: !newName.trim() || busy ? 0.6 : 1,
            }}
          >
            Add
          </button>
        </div>

        <div style={{ marginTop: 18 }}>
          <button
            type="button"
            data-testid="invoice-manage-months-done"
            onClick={() => onOpenChange(false)}
            style={{
              width: "100%", background: "#0F3B2E", color: "#fff", border: 0,
              borderRadius: 8, padding: "10px 0", fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}



function InvoiceSection({ testId, rows, total, onOpenAppointment, onToggleVerified, verifSavingId, monthsOpts, onChangeMonths, monthsSavingId, onManageMonths }) {
  return (
    <div data-testid={testId} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Summary table */}
      <div style={{
        background: "white", borderRadius: 14,
        padding: 20, maxWidth: "100%",
      }}>
        <HScroll>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 460 }}>
            <colgroup>
              <col style={{ width: "auto" }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 110 }} />
            </colgroup>
            <thead>
              <tr style={{ textAlign: "left", color: MUTED }}>
                <th style={{ padding: "8px 8px", fontWeight: 600, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase" }}>Category</th>
                <th style={{ padding: "8px 8px", fontWeight: 600, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "center" }}>Patients</th>
                <th style={{ padding: "8px 8px", fontWeight: 600, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "center" }}>Rate</th>
                <th style={{ padding: "8px 8px", fontWeight: 600, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "center" }}>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label} style={{ borderTop: `1px solid ${BORDER}` }}>
                  <td style={{ padding: "12px 8px", color: TEXT, fontWeight: 500, verticalAlign: "middle" }}>
                    <div style={{ lineHeight: 1.3 }}>
                      <div>{r.label}</div>
                      {r.note && (
                        <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
                          {r.note}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: "12px 8px", textAlign: "center", color: TEXT, verticalAlign: "middle" }}>{r.count}</td>
                  <td style={{ padding: "12px 8px", textAlign: "center", color: MUTED, verticalAlign: "middle" }}>{r.rate}</td>
                  <td style={{ padding: "12px 8px", textAlign: "center", color: TEXT, fontWeight: 600, verticalAlign: "middle" }}>
                    {fmtMoney(r.subtotal)}
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: `2px solid ${DARK_GREEN}` }}>
                <td colSpan={3} style={{ padding: "12px 8px", textAlign: "right", color: LABEL_GREEN, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", fontSize: 12 }}>
                  Subtotal
                </td>
                <td
                  data-testid={`${testId}-total`}
                  style={{
                    padding: "12px 8px", textAlign: "center", color: DARK_GREEN,
                    fontWeight: 700, fontSize: 16,
                    fontFamily: "'Work Sans', system-ui, sans-serif",
                  }}
                >
                  {fmtMoney(total)}
                </td>
              </tr>
            </tbody>
          </table>
        </HScroll>
      </div>

      {/* Details — one card per non-empty category */}
      {rows.map((r) => (
        r.patients.length > 0 && (
          <div
            key={r.label}
            style={{
              background: "white", borderRadius: 14,
              padding: 16, maxWidth: "100%",
            }}
          >
            <div style={{
              padding: "2px 0", fontSize: 13,
              color: r.color, fontWeight: 700,
              letterSpacing: "0.08em", textTransform: "uppercase",
              marginBottom: 4,
            }}>
              {r.label} · {r.patients.length} patient{r.patients.length === 1 ? "" : "s"}
            </div>
            <div style={{ marginTop: 10 }}>
              <SortableRowsTable
                sectionKey={`${testId}-${r.label.replace(/\W+/g, "_").toLowerCase()}`}
                patients={r.patients}
                showRecoveredCol={r.label.includes("Recovered")}
                isRecovered={r.label.includes("Recovered")}
                onOpenAppointment={onOpenAppointment}
                onToggleVerified={onToggleVerified}
                verifSavingId={verifSavingId}
                monthsOpts={monthsOpts}
                onChangeMonths={onChangeMonths}
                monthsSavingId={monthsSavingId}
                onManageMonths={onManageMonths}
              />
            </div>
          </div>
        )
      ))}
    </div>
  );
}
